import { inject, Injectable } from '@angular/core';
import { ExtractionTrainingData, PageRectangle, TrainingField, TrainingTableRow } from './model';
import { ExtractedLocationStoreService } from '../overlay/extracted-location-store.service';

import { associateWith, filterObject, keys, mapValues } from '@ps-gui/shared-utils';
import dayjs from 'dayjs';
import { TableOverlayStoreService } from '../overlay/table-overlay-store.service';
import {
  ExtractedRow,
  ExtractedTablesByKey,
  ExtractedValue,
  ExtractedValuesByKey,
  isTableReference,
  TableReference,
} from '../../models/extracted-values';
import {
  getFieldModel,
  TableColumnKey,
  TableKey,
} from '../../models/extraction-models/extraction-models';
import { ExtractedLocationData } from '../../models/interfaces';
import { OcrMultiSelection } from '../../models/ocr-selection-model';
import { TableData } from '../../table-overlay/table-overlay';
import { PersistDataService } from '../app-state/persist-data.service';
import { DocumentClassKey, WordRect } from '@ps-gui/claims-terminal-shared';
import { getUnionRect } from '../ocr/ocr-utils';
import { CtBatchStoreService } from '../app-state/ct-batch-store.service';

@Injectable({
  providedIn: 'root',
})
export class ExtractionTrainingDataService {
  private readonly extractedLocationStoreService = inject(ExtractedLocationStoreService);
  private readonly persistDataService = inject(PersistDataService);
  private readonly tableOverlayStoreService = inject(TableOverlayStoreService);
  private readonly ctBatchStoreService = inject(CtBatchStoreService);

  async getExtractionTrainingData(
    documentId: string,
    documentClass: DocumentClassKey,
  ): Promise<Omit<ExtractionTrainingData, 'documentId' | 'documentClass'>> {
    const { fields, tables } = await this.persistDataService.getExtractedDocumentDataSnapshot(
      documentId,
      documentClass,
    );
    return {
      fieldsByKeys: this.mapFieldsToTrainingFields(
        documentId,
        filterObject(
          fields,
          (key) =>
            !getFieldModel(this.ctBatchStoreService.ctBatchDomain, key).inlineConsolidatorForField,
        ) as ExtractedValuesByKey,
      ),
      tableRowsByTableKey: this.mapRowsToTrainingRows(documentId, tables),
    };
  }

  private mapFieldsToTrainingFields(
    documentId: string,
    fields: ExtractedValuesByKey,
  ): Record<string, TrainingField[]> {
    const fieldKeys = keys(fields);

    const locationDataByKey = associateWith(fieldKeys, (key) =>
      this.extractedLocationStoreService.getExtractedLocation(documentId, key),
    );

    const getExtractedField = (key: string, value: ExtractedValue): TrainingField | undefined => {
      const location = locationDataByKey[key];
      if (!value || !location) return undefined;
      return {
        value: this.getStringValue(value),
        confidence: 1,
        pageId: location.pageId,
        position: this.locationDataToExtractedPosition(location),
      };
    };

    return associateWith(fieldKeys, (fieldKey) =>
      [getExtractedField(fieldKey, fields[fieldKey]!)].filter((field) => field !== undefined),
    );
  }

  private mapRowsToTrainingRows(
    documentId: string,
    tables: ExtractedTablesByKey,
  ): Record<TableKey, TrainingTableRow[]> {
    const filteredTables = mapValues(tables, (_, rows) =>
      rows.filter((row) => !!(row.rowMembership as TableReference).tableId),
    );
    return mapValues(filteredTables, (_, extractedRows) =>
      extractedRows
        .map((extractedRow) => {
          const hasMeaningfulValue = (value: { value: unknown }) =>
            value.value != null && value.value !== '';
          extractedRow.rowData = Object.fromEntries(
            Object.entries(extractedRow.rowData).filter(([, cell]) => hasMeaningfulValue(cell)),
          );
          return extractedRow;
        })
        .map((extractedRow) =>
          mapValues(extractedRow.rowData, (columnKey, extractedCell) => {
            const tableData = this.tableOverlayStoreService.getTableById(
              (extractedRow.rowMembership as TableReference).tableId,
            );
            const tableCellLocation = this.getTableCellLocation(
              documentId,
              columnKey,
              extractedRow,
              tableData,
            );
            return {
              value: extractedCell.value!,
              confidence: 1,
              ...tableCellLocation!,
            };
          }),
        ),
    );
  }

  private getTableCellLocation(
    documentId: string,
    columnKey: TableColumnKey,
    tableRow: ExtractedRow,
    table: TableData,
  ): { position: PageRectangle; pageId: string } | undefined {
    const manuallyExtractedPosition = this.extractedLocationStoreService.getExtractedLocation(
      documentId,
      tableRow.rowData[columnKey].id,
    );
    if (manuallyExtractedPosition) {
      return {
        position: this.getPageRectangleForMultiSelection(manuallyExtractedPosition.selection),
        pageId: manuallyExtractedPosition.pageId,
      };
    }

    const tableId = isTableReference(tableRow.rowMembership)
      ? tableRow.rowMembership.tableId
      : undefined;
    const originalTableRowIndex = isTableReference(tableRow.rowMembership)
      ? tableRow.rowMembership.originalTableRowIndex
      : undefined;

    if (tableId === undefined || originalTableRowIndex === undefined) return undefined;

    const columnIndex = table.columnHeaders().findIndex((header) => header === columnKey);

    if (columnIndex < 0) {
      // this is the case when no column was labelled with the given fieldKey key
      return undefined;
    }

    return {
      position: {
        x0: table.lines.vertical()[columnIndex].offset(),
        x1: table.lines.vertical()[columnIndex + 1].offset(),
        y0: table.lines.horizontal()[originalTableRowIndex].offset(),
        y1: table.lines.horizontal()[originalTableRowIndex + 1].offset(),
      },
      pageId: table.pageId,
    };
  }

  private getStringValue(value: Date | number | string) {
    return value instanceof Date ? dayjs(value).format('DD.MM.YYYY') : String(value);
  }

  private locationDataToExtractedPosition(locationData: ExtractedLocationData): PageRectangle {
    return this.getPageRectangleForMultiSelection(locationData.selection);
  }

  private getPageRectangleForMultiSelection(selection: OcrMultiSelection) {
    return this.wordRectToPageRectangle(getUnionRect(selection[0]));
  }

  private wordRectToPageRectangle(wordRect: WordRect): PageRectangle {
    return {
      x0: wordRect.x[0],
      x1: wordRect.x[1],
      y0: wordRect.y[0],
      y1: wordRect.y[1],
    };
  }
}
